import { useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useStudentAssessmentsQuery } from "@/hooks/useStudents";
import { formatDate } from "@/utils/date";
import {
  assessmentRecordLabel,
  completedAssessmentsForStudent,
  studentWorkspacePaths,
} from "@/utils/student-workspace";
import { areAssessmentsComparable } from "@/utils/report-analysis";

export function ReportsListPage(): JSX.Element {
  const { studentId = "" } = useParams<{ studentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const assessmentsQuery = useStudentAssessmentsQuery(studentId);
  const requestedAssessmentId = searchParams.get("assessmentId") ?? "";
  const reports = completedAssessmentsForStudent(assessmentsQuery.data ?? [], studentId);
  const selectedAssessment = requestedAssessmentId
    ? reports.find((assessment) => assessment.id === requestedAssessmentId)
    : reports[0];

  useEffect(() => {
    if (!requestedAssessmentId && reports[0]) {
      const next = new URLSearchParams(searchParams);
      next.set("assessmentId", reports[0].id);
      setSearchParams(next, { replace: true });
    }
  }, [reports, requestedAssessmentId, searchParams, setSearchParams]);

  function selectAssessment(assessmentId: string): void {
    const next = new URLSearchParams(searchParams);
    next.set("assessmentId", assessmentId);
    setSearchParams(next);
  }

  const selectedVerified = selectedAssessment?.integrityStatus === "verified";
  const comparableReportCount = selectedAssessment
    ? reports.filter(
        (assessment) =>
          assessment.integrityStatus === "verified" &&
          areAssessmentsComparable(selectedAssessment, assessment),
      ).length
    : 0;

  return (
    <div className="space-y-6">
      <header className="overflow-hidden rounded-[1.75rem] border border-brand/20 bg-gradient-to-br from-white via-white to-brand/[0.06] shadow-[0_24px_56px_-38px_rgba(63,101,29,0.45)]">
        <div className="p-6 lg:p-7">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand">评估结果</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-800">评估报告</h1>
          <p className="mt-2 text-sm text-slate-500">
            查看统计分析、单次结果或多次评估的阶段变化。
          </p>
          {reports.length > 0 ? (
            <label className="mt-5 block max-w-xl text-sm font-semibold text-slate-700">
              选择评估记录
              <select
                value={selectedAssessment?.id ?? reports[0].id}
                onChange={(event) => selectAssessment(event.target.value)}
                className="mt-2 min-h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm font-normal text-slate-700 shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              >
                {reports.map((assessment) => (
                  <option key={assessment.id} value={assessment.id}>
                    {formatDate(assessment.submittedAt ?? assessment.startedAt)} ·{" "}
                    {assessmentRecordLabel()}
                    {assessment.integrityStatus === "verified" ? "" : " · 尚未校验"}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      </header>

      {assessmentsQuery.isLoading ? (
        <p className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          正在加载报告来源…
        </p>
      ) : null}
      {assessmentsQuery.isError ? (
        <p
          role="alert"
          className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center text-sm text-red-700"
        >
          报告数据加载失败，请稍后重试。
        </p>
      ) : null}

      {!assessmentsQuery.isLoading && !assessmentsQuery.isError && reports.length === 0 ? (
        <section className="rounded-2xl border border-dashed border-brand/30 bg-white p-10 text-center shadow-sm">
          <h2 className="text-lg font-semibold text-slate-800">暂无可用报告</h2>
          <p className="mt-2 text-sm text-slate-500">完成一份评估后，即可在这里查看分析和报告。</p>
          <Link
            to={studentWorkspacePaths.assessments(studentId)}
            className="mt-5 inline-flex min-h-11 items-center rounded-lg bg-brand px-5 text-sm font-semibold text-white hover:bg-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
          >
            前往评估
          </Link>
        </section>
      ) : null}

      {requestedAssessmentId && reports.length > 0 && !selectedAssessment ? (
        <section className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
          <h2 className="text-lg font-semibold text-red-800">评估记录不属于当前儿童</h2>
          <p className="mt-2 text-sm text-red-700">请从当前儿童的已完成评估中重新选择。</p>
          <button
            type="button"
            onClick={() => selectAssessment(reports[0].id)}
            className="mt-5 inline-flex min-h-11 items-center rounded-lg bg-brand px-5 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
          >
            选择最新记录
          </button>
        </section>
      ) : null}

      {selectedAssessment ? (
        <>
          <section aria-labelledby="report-types" className="space-y-4">
            <div>
              <h2 id="report-types" className="text-lg font-semibold text-slate-800">
                选择报告
              </h2>
            </div>
            <div className="grid gap-5 lg:grid-cols-3">
              <ReportTypeCard
                kind="analytics"
                title="统计分析"
                description="查看领域表现、得分结构与当前水平。"
                action="查看统计"
                to={studentWorkspacePaths.reportAnalytics(studentId, selectedAssessment.id)}
                disabled={!selectedVerified}
                disabledLabel="所选评估尚未通过校验"
              />
              <ReportTypeCard
                kind="single"
                title="单次报告"
                description="查看本次评估的领域得分与条目结果。"
                action="查看报告"
                to={studentWorkspacePaths.reportDetail(studentId, selectedAssessment.id)}
                disabled={!selectedVerified}
                disabledLabel="所选评估尚未通过校验"
              />
              <ReportTypeCard
                kind="compare"
                title="多次报告"
                description="对比 2 至 5 份同目录、同条目集合的评估。"
                action="选择对比记录"
                to={studentWorkspacePaths.reportCompare(studentId)}
                disabled={!selectedVerified || comparableReportCount < 2}
                disabledLabel={
                  selectedVerified ? "再完成 1 次同目录评估即可生成" : "所选评估尚未通过校验"
                }
              />
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

function ReportTypeCard({
  kind,
  title,
  description,
  action,
  to,
  disabled,
  disabledLabel,
}: {
  kind: "analytics" | "single" | "compare";
  title: string;
  description: string;
  action: string;
  to: string;
  disabled: boolean;
  disabledLabel: string;
}): JSX.Element {
  return (
    <article className="group relative isolate flex min-h-[22rem] flex-col overflow-hidden rounded-[1.75rem] bg-white p-7 shadow-[0_28px_62px_-40px_rgba(51,65,85,0.55)] ring-1 ring-brand/15 transition-[transform,box-shadow] duration-150 ease-out hover:-translate-y-1 hover:shadow-[0_34px_70px_-40px_rgba(63,101,29,0.45)]">
      <div className="relative h-28 overflow-hidden rounded-2xl bg-gradient-to-br from-brand/[0.11] via-brand/[0.035] to-white ring-1 ring-brand/10">
        <ReportCardIllustration kind={kind} />
      </div>
      <div className="mt-6 flex flex-1 flex-col">
        <p className="text-xs font-semibold tracking-[0.14em] text-brand">
          {kind === "analytics" ? "表现概览" : kind === "single" ? "本次结果" : "阶段变化"}
        </p>
        <h3 className="mt-2 text-xl font-semibold tracking-tight text-slate-800">{title}</h3>
        <p className="mt-3 flex-1 text-sm leading-6 text-slate-600">{description}</p>
      </div>
      {disabled ? (
        <span className="mt-6 inline-flex min-h-11 items-center justify-center rounded-xl bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-500 ring-1 ring-inset ring-slate-200">
          {disabledLabel}
        </span>
      ) : (
        <Link
          to={to}
          className="mt-6 inline-flex min-h-11 items-center justify-center rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-white shadow-[0_12px_24px_-15px_rgba(63,101,29,0.95)] transition-colors duration-150 hover:bg-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2"
        >
          {action}
        </Link>
      )}
    </article>
  );
}

function ReportCardIllustration({
  kind,
}: {
  kind: "analytics" | "single" | "compare";
}): JSX.Element {
  if (kind === "analytics") {
    return (
      <svg
        viewBox="0 0 320 112"
        className="h-full w-full text-brand"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M28 25H292M28 56H292M28 87H292" stroke="currentColor" strokeOpacity=".12" />
        <rect x="52" y="58" width="36" height="30" rx="8" fill="currentColor" fillOpacity=".22" />
        <rect x="106" y="40" width="36" height="48" rx="8" fill="currentColor" fillOpacity=".38" />
        <rect x="160" y="25" width="36" height="63" rx="8" fill="currentColor" fillOpacity=".58" />
        <rect x="214" y="49" width="36" height="39" rx="8" fill="currentColor" fillOpacity=".3" />
        <path
          d="M48 73L124 52L178 39L232 58L276 31"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="3"
        />
        <circle cx="276" cy="31" r="5" fill="white" stroke="currentColor" strokeWidth="3" />
      </svg>
    );
  }

  if (kind === "single") {
    return (
      <svg
        viewBox="0 0 320 112"
        className="h-full w-full text-brand"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M28 28H95M28 56H76M28 84H110"
          stroke="currentColor"
          strokeLinecap="round"
          strokeOpacity=".16"
          strokeWidth="2"
        />
        <rect
          x="122"
          y="14"
          width="112"
          height="84"
          rx="18"
          fill="white"
          fillOpacity=".72"
          stroke="currentColor"
          strokeOpacity=".24"
        />
        <path
          d="M150 39H206M150 56H206M150 73H190"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="5"
          strokeOpacity=".34"
        />
        <circle cx="254" cy="76" r="22" fill="currentColor" fillOpacity=".16" />
        <path
          d="M244 76L251 83L265 68"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="4"
        />
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 320 112"
      className="h-full w-full text-brand"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M30 24H290M30 56H290M30 88H290" stroke="currentColor" strokeOpacity=".12" />
      <path
        d="M42 82L96 58L150 69L212 30L278 45"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeOpacity=".34"
        strokeWidth="3"
      />
      <path
        d="M42 70L96 76L150 42L212 61L278 24"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="4"
      />
      <circle cx="42" cy="70" r="5" fill="white" stroke="currentColor" strokeWidth="3" />
      <circle cx="150" cy="42" r="5" fill="white" stroke="currentColor" strokeWidth="3" />
      <circle cx="278" cy="24" r="5" fill="white" stroke="currentColor" strokeWidth="3" />
    </svg>
  );
}
